/**
 * Shared chart utilities for timezone conversion, labels, and data building.
 * Extracted from stats.jsx, DashboardChart.jsx, and CampaignResultsPage.jsx (Phase 6).
 */

/** Backend stores hourly buckets in UTC; convert to local timezone for display. */
export function utcHourlyToLocal(utcBuckets) {
  const offsetMinutes = new Date().getTimezoneOffset();
  const localBuckets = new Array(24).fill(0);
  for (let u = 0; u < 24; u++) {
    const localMinutes = (u * 60 - offsetMinutes + 24 * 60) % (24 * 60);
    const localHourIndex = Math.floor(localMinutes / 60);
    localBuckets[localHourIndex] += utcBuckets[u] || 0;
  }
  return localBuckets;
}

export const HOURLY_LABELS = [
  '12am', '1am', '2am', '3am', '4am', '5am',
  '6am', '7am', '8am', '9am', '10am', '11am',
  '12pm', '1pm', '2pm', '3pm', '4pm', '5pm',
  '6pm', '7pm', '8pm', '9pm', '10pm', '11pm'
];

export const TIME_BLOCKS = [
  { label: '12am-4am', hours: [0, 1, 2, 3] },
  { label: '4am-8am', hours: [4, 5, 6, 7] },
  { label: '8am-12pm', hours: [8, 9, 10, 11] },
  { label: '12pm-4pm', hours: [12, 13, 14, 15] },
  { label: '4pm-8pm', hours: [16, 17, 18, 19] },
  { label: '8pm-12am', hours: [20, 21, 22, 23] }
];

export const BLOCK_LABELS = TIME_BLOCKS.map(b => b.label);

/**
 * When hourly breakdown is all zeros but we have totals, place totals in the
 * current local hour/block so the chart shows activity at "now".
 */
export function applyHourlyFallback(hourly, total, showHourlyDetail) {
  if (total <= 0 || !hourly.every(v => v === 0)) return hourly;

  const result = [...hourly];
  const currentLocalHour = new Date().getHours();

  if (showHourlyDetail) {
    result[currentLocalHour] = total;
  } else {
    const blockIndex = TIME_BLOCKS.findIndex(b => b.hours.includes(currentLocalHour));
    result[blockIndex >= 0 ? blockIndex : result.length - 1] = total;
  }
  return result;
}

/**
 * Build chart arrays from dailyStats for a given view.
 *
 * @param {Array} dailyStats - Array of daily stat objects with date, plays, ctas, etc.
 * @param {'today'|'7days'|'30days'|'90days'} view
 * @param {boolean} [showHourlyDetail=true] - For "today" view: 24 hourly points vs 6 blocks
 * @returns {{ playCounts, ctaCounts, leadCounts, chartLabels }}
 */
export function buildChartData(dailyStats, view, showHourlyDetail = true) {
  if (view === 'today') {
    return buildTodayChart(dailyStats, showHourlyDetail);
  }

  const days = view === '30days' ? 30 : view === '90days' ? 90 : 7;

  if (view === '90days') {
    return buildWeeklyChart(dailyStats, 13);
  }

  return buildDailyChart(dailyStats, days);
}

// --- Internal helpers ---

function buildTodayChart(dailyStats, showHourlyDetail) {
  const todayKey = new Date().toISOString().split('T')[0];
  const todayStats = dailyStats.find(d => d.date === todayKey) || {};

  const hourlyPlays = utcHourlyToLocal(todayStats.hourlyPlays || new Array(24).fill(0));
  const hourlyCtas = utcHourlyToLocal(todayStats.hourlyCtas || new Array(24).fill(0));
  const hourlyLeads = utcHourlyToLocal(todayStats.hourlyLeads || new Array(24).fill(0));
  const totalPlaysToday = todayStats.plays || 0;

  let playCounts, ctaCounts, leadCounts, chartLabels;

  if (showHourlyDetail) {
    chartLabels = HOURLY_LABELS;
    playCounts = hourlyPlays;
    ctaCounts = hourlyCtas;
    leadCounts = hourlyLeads;
  } else {
    chartLabels = BLOCK_LABELS;
    playCounts = TIME_BLOCKS.map(b => b.hours.reduce((sum, h) => sum + (hourlyPlays[h] || 0), 0));
    ctaCounts = TIME_BLOCKS.map(b => b.hours.reduce((sum, h) => sum + (hourlyCtas[h] || 0), 0));
    leadCounts = TIME_BLOCKS.map(b => b.hours.reduce((sum, h) => sum + (hourlyLeads[h] || 0), 0));
  }

  // Fallback: put totals in current hour/block when hourly breakdown is all zeros
  playCounts = applyHourlyFallback(playCounts, totalPlaysToday, showHourlyDetail);
  ctaCounts = applyHourlyFallback(ctaCounts, todayStats.ctas || 0, showHourlyDetail);
  leadCounts = applyHourlyFallback(leadCounts, todayStats.leads || todayStats.leaderboardJoins || 0, showHourlyDetail);

  return { playCounts, ctaCounts, leadCounts, chartLabels };
}

function buildDailyChart(dailyStats, days) {
  const today = new Date();
  const dates = Array.from({ length: days }, (_, idx) => {
    const d = new Date(today);
    d.setDate(d.getDate() - (days - 1 - idx));
    return d.toISOString().split('T')[0];
  });
  const dailyMap = new Map(dailyStats.map(d => [d.date, d]));

  return {
    playCounts: dates.map(date => dailyMap.get(date)?.plays || 0),
    ctaCounts: dates.map(date => dailyMap.get(date)?.ctas || 0),
    leadCounts: dates.map(date => dailyMap.get(date)?.leads || dailyMap.get(date)?.leaderboardJoins || 0),
    chartLabels: dates
  };
}

function buildWeeklyChart(dailyStats, weeks) {
  const today = new Date();
  const dailyMap = new Map(dailyStats.map(d => [d.date, d]));
  const weeklyData = [];

  for (let w = weeks - 1; w >= 0; w--) {
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() - w * 7);
    const weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() - 6);

    let plays = 0, ctas = 0, leads = 0;
    for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().split('T')[0];
      const day = dailyMap.get(key);
      if (day) {
        plays += day.plays || 0;
        ctas += day.ctas || 0;
        leads += day.leads || day.leaderboardJoins || 0;
      }
    }

    const label = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    weeklyData.push({ label, plays, ctas, leads });
  }

  return {
    playCounts: weeklyData.map(w => w.plays),
    ctaCounts: weeklyData.map(w => w.ctas),
    leadCounts: weeklyData.map(w => w.leads),
    chartLabels: weeklyData.map(w => w.label)
  };
}
